import { MemberRollupDto } from '../../types/opsBriefing';

/**
 * 회원 단위 요약 문구 — **한 곳에서만 만듭니다.**
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 카드는 여전히 수강권 단위입니다. 이건 그 위에 얹는 한 줄입니다
 * ─────────────────────────────────────────────────────────────
 * 서버는 활성 수강권 한 건당 카드 한 장을 줍니다. 수강권 3건인 회원은 목록에
 * 세 줄로 뜨고, 각 줄은 **자기 수강권의** 잔여·만료·금액을 말합니다.
 * 그 셋을 가르는 것이 `cardKey` 이고, 합치면 원장이 누른 줄과 다른 줄이 열립니다
 * (`adapters.cardKey` 주석의 실제 사고 — 0원짜리 줄을 눌렀는데 132만원이 떴습니다).
 *
 * 그래서 **합치지 않고 덧붙입니다.** "이 줄은 20회권이고, 이 회원 전체로는
 * 잔여 N회에 다른 수강권이 N건 더 있다".
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 「일부 미집계」를 반드시 말합니다
 * ─────────────────────────────────────────────────────────────
 * `remainTotal` 은 두 종류를 빼고 낸 합계입니다.
 *   · 무제한(대관·월정액) — 999회를 더하면 허수가 됩니다 (`sessionless.ts`)
 *   · 회차 정보가 온전하지 않은 건 — `remainUncountedCount`
 * 뺐다는 사실을 안 적으면 원장님은 그 숫자를 **회원의 전부**로 읽습니다.
 * 이 저장소가 「금액 산정 불가 N명」을 따로 밝히는 것과 같은 규칙입니다.
 */

export type RollupSummary = {
    /** 목록에 붙는 짧은 꼬리표 — 「외 2건」 */
    tag: string;
    /** 상세·툴팁에 쓰는 전체 문장 */
    detail: string;
    /** 합계가 전체가 아닌가 (무제한·미집계가 섞였는가) */
    partial: boolean;
};

/**
 * 요약할 게 있는가.
 *
 * 수강권이 한 건뿐이고 특이사항도 없으면 **null 을 돌려줍니다** —
 * 그 줄이 이미 그 수강권의 잔여를 말하고 있어서, 같은 숫자를 한 번 더 적으면
 * 화면만 시끄러워지고 원장님은 두 숫자가 왜 있는지 묻게 됩니다.
 */
export const summarizeRollup = (r: MemberRollupDto | undefined): RollupSummary | null => {
    if (!r) return null;

    const others = r.otherMembershipCount;
    const partial = r.remainUncountedCount > 0 || r.sessionlessCount > 0;
    if (others <= 0 && !partial) return null;

    const parts: string[] = [];

    /*
     * 잔여 합계. `null` 은 「0회」가 아니라 **셀 수 있는 수강권이 없다**는 뜻입니다.
     * 0 으로 적으면 「다 쓴 회원」이 되는데 실제로는 무제한만 갖고 있을 수 있습니다.
     */
    parts.push(
        r.remainTotal == null
            ? '회원 전체 잔여 산정 불가'
            : `회원 전체 잔여 ${r.remainTotal}회`
    );

    if (others > 0) parts.push(`다른 수강권 ${others}건`);
    if (r.sessionlessCount > 0) parts.push(`무제한 ${r.sessionlessCount}건 별도`);
    if (r.remainUncountedCount > 0) {
        parts.push(`${r.remainUncountedCount}건 회차 미집계`);
    }

    return {
        tag: others > 0 ? `외 ${others}건` : '일부 미집계',
        detail: parts.join(' · '),
        partial,
    };
};

/** 목록의 좁은 칸에 들어가는 한 줄 — 툴팁이 전체 문장을 맡습니다 */
export const ROLLUP_PARTIAL_NOTE =
    '무제한·회차 미집계 수강권은 합계에서 빠져 있습니다';
